<template>
    <q-expansion-item>
        <template #header>
            <q-item-section avatar>
                <q-radio
                    color="deep-orange"
                    :model-value="subCustomerId"
                    @update:model-value="($event) => updateSubCustomerId(isSubCustomer ? subCustomer : null)"
                    :val="isSubCustomer ? customerModel.id : null"
                />
            </q-item-section>
            <q-item-section>
                <q-item-label>
                    {{ customerModel.contact.org_name }}
                </q-item-label>
                <q-item-label caption>
                    {{ customerModel.work_types.length }} Work Type{{ customerModel.work_types.length != 1 ? 's' : '' }}
                    <template v-if="isSubCustomer && !(customerModel.work_types ?? []).length">
                        <q-badge color="blue-grey-6" style="cursor:default;">
                            +{{ customer.work_types.length }} From {{ customer.contact.org_name }}
                            <q-tooltip>
                                {{ customer.contact.org_name }}'s work types can be used on {{ customerModel.contact.org_name }} because {{ customerModel.contact.org_name }} has no work types.
                            </q-tooltip>
                        </q-badge>
                    </template>
                </q-item-label>
            </q-item-section>
            <q-item-section side>
                <q-item-label>
                    <q-badge color="blue-grey-6" v-if="originalSubCustomerId == (isSubCustomer ? customerModel.id : null)">
                        Original Selection
                    </q-badge>
                </q-item-label>
                <q-item-label>
                    <q-badge color="deep-orange" v-if="subCustomerId == (isSubCustomer ? customerModel.id : null)">
                        Customer Selected
                    </q-badge>
                    <!-- If the work type also exists -->
                    <q-icon 
                        v-if="
                            subCustomerId == (isSubCustomer ? customerModel.id : null) && 
                            customerModel.work_types.findIndex(v => v.id === workTypeId) !== -1
                        " name="mdi-arrow-right-bold" 
                    />
                    <!-- Show which work type is selected -->
                    <q-badge color="primary" v-if="customerModel.work_types.findIndex(v => v.id === workTypeId) !== -1">
                        {{ customerModel.work_types.find(v => v.id === workTypeId).name }}
                        <q-tooltip>
                            The selected Work Type
                        </q-tooltip>
                    </q-badge>
                </q-item-label>
            </q-item-section>
        </template>

        <q-card :style="`background-color: ${$q.dark.isActive ? '#0f0f0f' : '#eeeeee'};`">
            <q-scroll-area visible style="min-height: none;height: calc(20vh);max-width: 100vw;width: 100%;">
                <q-list dense class="q-mr-md" :separator="$q.dark.isActive">
                    <template v-for="i in ((isSubCustomer && !this.customerModel.work_types.length) ? 2 : 1)">
                        <q-item
                            v-ripple
                            tag="label"
                            v-for="(workType) in ((i == 1 && isSubCustomer) ? subCustomer.work_types : customer.work_types)"
                            :class="{'bg-dark-page': $q.dark.isActive}"
                        >
                            <q-item-section avatar>
                                <q-radio 
                                    :model-value="workTypeId"
                                    @update:model-value="updateWorkTypeId(isSubCustomer ? subCustomer : null, workType)"
                                    :val="workType.id"
                                />
                            </q-item-section>
                            <q-item-section>
                                <q-item-label>
                                    <template v-if="i == 2 && isSubCustomer">
                                        <q-badge color="deep-orange-12" dense>
                                            <template #icon>
                                                From:
                                            </template>
                                            {{ customer.contact.org_name }}
                                        </q-badge>

                                        <q-icon class="q-ml-xs text-blue-grey-11" name="mdi-arrow-right-bold" />

                                        <q-tooltip>
                                            {{ customer.contact.org_name }}'s work types can be used on {{ customerModel.contact.org_name }} because {{ customerModel.contact.org_name }} has no work types.
                                        </q-tooltip>
                                    </template>
                                    {{ workType.name }}
                                </q-item-label>
                                <q-item-label v-if="workType.description ?? false" caption>
                                    {{ workType.description }}
                                </q-item-label>
                            </q-item-section>
                            <q-item-section v-if="originalWorkTypeId == workType.id">
                                <q-item-label>
                                    <q-badge color="blue-grey-6">
                                        Original Selection
                                    </q-badge>
                                </q-item-label>
                            </q-item-section>
                            <q-item-section side>
                                <q-item-label>
                                    <q-chip
                                        square
                                        size="0.65rem"
                                        v-for="modifier in workType.modifiers"
                                        :color="modifierColors[modifier.type.column]"
                                        text-color="white"
                                    >
                                        <q-icon :name="containerDetails.find(v => v.name === modifier.type.column).icon" />

                                        <q-tooltip>
                                            {{ modifier.type.name }}
                                        </q-tooltip>
                                    </q-chip>
                                </q-item-label>
                            </q-item-section>
                        </q-item>
                    </template>
                </q-list>
            </q-scroll-area>
        </q-card>
    </q-expansion-item>
</template>

<script>
export default {
    name: 'CustomerWorkTypes',
    emits: [
        'customerChange',
        'workTypeChange',
        'update:subCustomerId', 
        'update:workTypeId'
    ],
    props: {
        customer: {
            type: Object,
            required: true,
        },
        subCustomer: {
            type: Object,
            default: null,
        },
        subCustomerId: {
            type: Number,
            default: null,
        },
        workTypeId: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            originalWorkTypeId: this.workTypeId,
            originalSubCustomerId: this.subCustomerId,

            selected: {
                workType: null,
                subCustomer: null,
            }
        };
    },
    methods: {
        updateWorkTypeId(subCustomer, workType) {
            this.selected.workType = workType;
            this.selected.subCustomer = subCustomer;

            if (subCustomer !== null) {
                this.$emit('customerChange', subCustomer);
                this.$emit('update:subCustomerId', subCustomer.id);
            }
            this.$emit('workTypeChange', workType);
            this.$emit('update:workTypeId', workType.id);
        },
        updateSubCustomerId(customerModel) {
            const isCustomerWorkType = this.customer.work_types.findIndex(v => v.id === this.workTypeId) > -1;
            // If sub customer, and sub customer have no work types 
            // then we want to be able to select the main customer's work types.
            if ( isCustomerWorkType && (this.isSubCustomer && !this.subCustomer.work_types.length) ) {
                this.$emit('update:subCustomerId', customerModel?.id ?? null);
                this.$emit('customerChange', customerModel);

                // if (this.customer.work_types.length > 0) {
                //     this.$emit('workTypeChange', this.customer.work_types[0].id);
                //     this.$emit('update:workTypeId', this.customer.work_types[0].id);
                // }
                return;
            }

            this.$emit('update:workTypeId', this.customerModel?.work_types[0]?.id ?? this.customer.work_types[0].id);
            this.$emit('workTypeChange', this.customerModel?.work_types[0]?.id ?? this.customer.work_types[0].id);

            this.$emit('update:subCustomerId', customerModel?.id ?? null);
            this.$emit('customerChange', customerModel);
        }
    },
    computed: {
        customerModel() {
            return this.isSubCustomer ? this.subCustomer : this.customer;
        },
        isSubCustomer() {
            return (this.subCustomer ?? false)
                ? true : false;
        },
        containerDetails() {
            return [
                {
                    name: 'container_number',
                    label: 'Container',
                    icon: 'mdi-truck-cargo-container',
                },
                {
                    name: 'sku_count',
                    label: 'Splits',
                    icon: 'mdi-clipboard-list',
                },
                {
                    name: 'pallet_count',
                    label: 'Pallets',
                    icon: 'mdi-shipping-pallet',
                },
                {
                    name: 'piece_count',
                    label: 'Pieces',
                    icon: 'mdi-cube',
                },
                {
                    name: 'hours',
                    label: 'Hours',
                    icon: 'mdi-clock-outline',
                },
                {
                    name: 'weight',
                    label: 'Weight',
                    icon: 'mdi-weight',
                },
                {
                    name: 'time_delay',
                    label: 'Time Delay',
                    icon: 'mdi-clock-alert-outline',
                },
            ];
        }
    },
}
</script>